import React, { useEffect, useState } from 'react';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { toShortAmPm } from '../../../utilities/common/commonFuntion';
import { DAYS, ParticipantResourceStatus, ParticipantResourceStatusOptions } from '../../../Data/SD';
import { CreateParticipantResourceDto } from '../../../interfaces/participantResource/participantResource';
import Region from '../../../interfaces/identity/Region';
import { useAuth } from '../../../firebase/AuthProvider';
import { toast } from 'react-toastify';
import { getAllRegions } from '../../../services/userServices/UserInfoService';
import Select, { SingleValue } from 'react-select';
import { CreateParticipantResourceAsync } from '../../../services/ParticipantResourceService/ParticipantResouceService';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';



const AddParticipantResource = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        Title: '',
        Description: '',
        PhoneNumber: '',
        AddressLine1: '',
        AddressLine2: '',
        City: '',
        State: '',
        PostalCode: '',
        RegionId: 0,
        Status: '',
        HoursNotes: '',
        Notes: ''
    });

    const [hoursFields, setHoursFields] = useState<
        { startDay: string; endDay: string; startTime: string; endTime: string }[]
    >([]);
    const [errors, setErrors] = useState<any>({});

    const [regions, setRegions] = useState<Region[]>([]);

    // loading
    const [isLoading, seIsLoading] = useState({
        region: false,
        creating: false,
    });

    useEffect(() => {
        getRegions();
    }, []);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: undefined });
    };

    const handleHoursChange = (index: number, field: string, value: string) => {
        const updated = [...hoursFields];
        updated[index][field as keyof typeof updated[0]] = value;
        // If startDay changes, reset endDay if it's before startDay
        if (field === 'startDay' && updated[index].endDay) {
            const startIdx = DAYS.indexOf(value);
            const endIdx = DAYS.indexOf(updated[index].endDay);
            if (endIdx < startIdx) updated[index].endDay = '';
        }
        setHoursFields(updated);
        setErrors({ ...errors, [`HoursJsonb.${index}.${field}`]: undefined });
    };

    const addHours = () =>
        setHoursFields([...hoursFields, { startDay: '', endDay: '', startTime: '', endTime: '' }]);
    const removeHours = (i: number) =>
        setHoursFields(hoursFields.filter((_, idx) => idx !== i));

    // Helper to check for duplicate day ranges
    const isDuplicateRange = (start: string, end: string, idx: number) => {
        return hoursFields.some(
            (h, i) =>
                i !== idx &&
                h.startDay === start &&
                (h.endDay || '') === (end || '')
        );
    };

    // Helper to compare times in "HH:mm" format
    const isStartTimeBeforeEndTime = (start: string, end: string) => {
        if (!start || !end) return true;
        return start < end;
    };

    const validate = () => {
        const e: any = {};

        // All fields required
        Object.entries(form).forEach(([k, v]) => {
            if (!v) e[k] = `${k} is required.`;
        });

        if (form.PhoneNumber && !/^\+?[0-9\s\-]{7,15}$/.test(form.PhoneNumber))
            e.PhoneNumber = 'Phone number format is invalid.';

        if (form.PostalCode && !/^\d{4,10}$/.test(form.PostalCode))
            e.PostalCode = 'Postal code format is invalid.';

        if (
            form.Status &&
            ![
                ParticipantResourceStatus.Accepting,
                ParticipantResourceStatus.WaitlistOnly,
                ParticipantResourceStatus.NotAccepting,
            ].includes(form.Status as ParticipantResourceStatus)
        )
            e.Status = 'Invalid status.';

        // HoursJsonb validation
        if (hoursFields.length === 0) {
            e.HoursJsonb = 'At least one hours entry is required.';
        }

        hoursFields.forEach((item, idx) => {
            if (!item.startDay) e[`HoursJsonb.${idx}.startDay`] = 'Start day required.';
            const startIdx = DAYS.indexOf(item.startDay);
            if (item.endDay) {
                const endIdx = DAYS.indexOf(item.endDay);
                if (endIdx <= startIdx) e[`HoursJsonb.${idx}.endDay`] = 'End day must be after start day.';
            }
            if (!item.startTime) e[`HoursJsonb.${idx}.startTime`] = 'Start time required.';
            if (!item.endTime) e[`HoursJsonb.${idx}.endTime`] = 'End time required.';
            if (
                item.startTime &&
                item.endTime &&
                !isStartTimeBeforeEndTime(item.startTime, item.endTime)
            )
                e[`HoursJsonb.${idx}.endTime`] = 'End time must be after start time.';
            if (isDuplicateRange(item.startDay, item.endDay, idx))
                e[`HoursJsonb.${idx}.startDay`] = 'Duplicate day range.';
        });

        return e;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const v = validate();
        setErrors(v);

        if (Object.keys(v).length > 0) return;

        // Build HoursJsonb object with AM/PM format
        const hoursObj: Record<string, string> = {};
        hoursFields.forEach(h => {
            const key = h.endDay ? `${h.startDay}-${h.endDay}` : h.startDay;
            hoursObj[key] = `${toShortAmPm(h.startTime)}-${toShortAmPm(h.endTime)}`;
        });

        const finalData: CreateParticipantResourceDto = {
            title: form.Title,
            description: form.Description,
            phoneNumber: form.PhoneNumber,
            addressLine1: form.AddressLine1,
            addressLine2: form.AddressLine2,
            city: form.City,
            state: form.State,
            postalCode: form.PostalCode,
            regionId: form.RegionId,
            status: form.Status,
            hoursNotes: form.HoursNotes,
            notes: form.Notes,
            hoursJsonb: JSON.stringify(hoursObj)
        };

        console.log("data : ", finalData);

        const token = await currentUser?.getIdToken();
        if (!token) return;

        try {
            seIsLoading(prev => ({ ...prev, creating: true }));
            const res = await CreateParticipantResourceAsync(token, finalData);
            if (res.success) {
                toast.success(res.message);
                navigate("/dashboard/owner/resources")
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to saving the Resource");
        } finally { seIsLoading(prev => ({ ...prev, creating: false })) }
    };

    const getRegions = async () => {
        if (!currentUser) return;
        seIsLoading(prev => ({ ...prev, region: true }));

        const token = await currentUser.getIdToken();
        try {
            const res = await getAllRegions(token);
            if (res.success) {
                setRegions(res.data);
            }
        } catch (e) {
            toast.error("Failed to load regions");
        } finally { seIsLoading(prev => ({ ...prev, region: false })); }

    };

    // Convert regions to options for React Select
    const regionOptions = regions.map(region => ({
        value: region.regionId,
        label: region.regionName
    }));

    // Handler for React Select
    const handleRegionChange = (selected: SingleValue<{ value: number; label: string }>) => {
        setForm({ ...form, RegionId: selected ? Number(selected.value) : 0 });
        setErrors({ ...errors, RegionId: undefined });
    };

    return (
        <div className="common_card">
            <div className="top top_bar_flx">
                <div className="top_bar_tittle">
                    <h2>Add Participant Resource  </h2>
                </div>
            </div>

            <Form onSubmit={handleSubmit}>

                {/* Row 1 */}
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Title</Form.Label>
                            <Form.Control name="Title" value={form.Title} onChange={handleChange} placeholder='Enter Title..'/>
                            {errors.Title && <p className="text-red-500 text-sm mt-1" >{errors.Title}</p>}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control name="Description" value={form.Description} onChange={handleChange} placeholder='Enter Descriiption..'/>
                            {errors.Description && <p className="text-red-500 text-sm mt-1">{errors.Description}</p>}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control type='number' name="PhoneNumber" value={form.PhoneNumber} onChange={handleChange} placeholder='Enter PhoneNumber..'/>
                            {errors.PhoneNumber && <p className="text-red-500 text-sm mt-1">{errors.PhoneNumber}</p>}
                        </Form.Group>
                    </Col>
                </Row>

                {/* Row 2 */}
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Address Line 1</Form.Label>
                            <Form.Control name="AddressLine1" value={form.AddressLine1} onChange={handleChange} placeholder='Enter Address Line 1..'/>
                            {errors.AddressLine1 && <p className="text-red-500 text-sm mt-1">{errors.AddressLine1}</p>}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Address Line 2</Form.Label>
                            <Form.Control name="AddressLine2" value={form.AddressLine2} onChange={handleChange} placeholder='Enter Address Line 2..'/>
                            {errors.AddressLine2 && <p className="text-red-500 text-sm mt-1">{errors.AddressLine2}</p>}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>City</Form.Label>
                            <Form.Control name="City" value={form.City} onChange={handleChange} placeholder='Enter City..'/>
                            {errors.City && <p className="text-red-500 text-sm mt-1">{errors.City}</p>}
                        </Form.Group>
                    </Col>
                </Row>

                {/* Row 3 */}
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>State</Form.Label>
                            <Form.Control name="State" value={form.State} onChange={handleChange} placeholder='Enter State..'/>
                            {errors.State && <p className="text-red-500 text-sm mt-1">{errors.State}</p>}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Postal Code</Form.Label>
                            <Form.Control type='number' name="PostalCode" value={form.PostalCode} onChange={handleChange} placeholder='Enter PostalCode..' />
                            {errors.PostalCode && <p className="text-red-500 text-sm mt-1">{errors.PostalCode}</p>}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Region</Form.Label>
                            <Select
                                options={regionOptions}
                                value={regionOptions.find(opt => opt.value === Number(form.RegionId))}
                                onChange={handleRegionChange}
                                isLoading={isLoading.region}
                                isClearable
                                classNamePrefix="react-select"
                                placeholder="Select Region"
                                name="RegionId"
                            />
                            {errors.RegionId && <p className="text-red-500 text-sm mt-1">{errors.RegionId}</p>}
                        </Form.Group>
                    </Col>
                </Row>

                {/* Row 4 */}
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Status</Form.Label>
                            <Form.Select name="Status" value={form.Status} onChange={handleChange}>
                                <option value="">Select</option>
                                {ParticipantResourceStatusOptions.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </Form.Select>
                            {errors.Status && <p className="text-red-500 text-sm mt-1">{errors.Status}</p>}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Hours Notes</Form.Label>
                            <Form.Control name="HoursNotes" value={form.HoursNotes} onChange={handleChange} placeholder='Enter Hours Notes..'/>
                            {errors.HoursNotes && <p className="text-red-500 text-sm mt-1">{errors.HoursNotes}</p>}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Notes</Form.Label>
                            <Form.Control name="Notes" value={form.Notes} onChange={handleChange} placeholder='Enter Notes..' />
                            {errors.Notes && <p className="text-red-500 text-sm mt-1">{errors.Notes}</p>}
                        </Form.Group>
                    </Col>
                </Row>

                {/* HoursJsonb */}
                <hr />
                <h5>Hours (add multiple)</h5>
                {errors.HoursJsonb && (
                    <p className="text-red-500 text-sm mt-1">{errors.HoursJsonb}</p>
                )}
                {hoursFields.map((f, i) => (
                    <Row key={i}>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Start Day</Form.Label>
                                <Form.Select
                                    value={f.startDay}
                                    onChange={e => handleHoursChange(i, 'startDay', e.target.value)}
                                >
                                    <option value="">Select</option>
                                    {DAYS.map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </Form.Select>
                                {errors[`HoursJsonb.${i}.startDay`] && (
                                    <p className="text-red-500 text-sm mt-1">{errors[`HoursJsonb.${i}.startDay`]}</p>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>End Day (optional)</Form.Label>
                                <Form.Select
                                    value={f.endDay}
                                    onChange={e => handleHoursChange(i, 'endDay', e.target.value)}
                                    disabled={!f.startDay}
                                >
                                    <option value="">(none)</option>
                                    {DAYS
                                        .filter(d => f.startDay && DAYS.indexOf(d) > DAYS.indexOf(f.startDay))
                                        .map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                </Form.Select>
                                {errors[`HoursJsonb.${i}.endDay`] && (
                                    <p className="text-red-500 text-sm mt-1">{errors[`HoursJsonb.${i}.endDay`]}</p>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Start Time</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={f.startTime}
                                    onChange={e => handleHoursChange(i, 'startTime', e.target.value)}
                                />
                                {errors[`HoursJsonb.${i}.startTime`] && (
                                    <p className="text-red-500 text-sm mt-1">{errors[`HoursJsonb.${i}.startTime`]}</p>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>End Time</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={f.endTime}
                                    onChange={e => handleHoursChange(i, 'endTime', e.target.value)}
                                />
                                {errors[`HoursJsonb.${i}.endTime`] && (
                                    <p className="text-red-500 text-sm mt-1">{errors[`HoursJsonb.${i}.endTime`]}</p>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                            <Button variant="danger" onClick={() => removeHours(i)}>Remove</Button>
                        </Col>
                    </Row>
                ))}
                <Button className="mt-2" variant="secondary" onClick={addHours}>
                    Add Hours
                </Button>
                <hr />
                <div className="form_btn_flx mby_t_24">
                    <Button type="submit" variant="primary" disabled={isLoading.creating}>
                        {isLoading.creating ? <span><Spinner size='sm' /> Creating... </span> : "Create"}
                    </Button>
                </div>
            </Form>
        </div>
    );
}

export default AddParticipantResource;
